import React from "react";
import {
  BookOpen,
  Cpu,
  ChartNoAxesCombined,
  Users,
} from "lucide-react";

import "./GrowthSection.css";

const growthSteps = [
  {
    number: "01",
    title: "Learn",
    icon: BookOpen,
    description:
      "Develop expertise through real-world semiconductor projects, technical collaboration, and continuous exposure to new technologies.",
  },
  {
    number: "02",
    title: "Build",
    icon: Cpu,
    description:
      "Contribute to challenging engineering programs across the semiconductor development lifecycle and transform ideas into reliable silicon solutions.",
  },
  {
    number: "03",
    title: "Grow",
    icon: ChartNoAxesCombined,
    description:
      "Expand your technical capabilities, take on greater responsibilities, and progress through continuous learning and practical experience.",
  },
  {
    number: "04",
    title: "Lead",
    icon: Users,
    description:
      "Build the confidence to mentor others, contribute to engineering excellence, and help shape the success of future projects and teams.",
  },
];

const GrowthSection: React.FC = () => {
  return (
    <section className="growthX">
      {/* BACKGROUND */}
      <div className="growthX__grid" />
      <div className="growthX__blueGlow" />
      <div className="growthX__goldGlow" />

      {/* Semiconductor artwork */}
      <svg
        className="growthX__circuit"
        viewBox="0 0 1000 520"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="traceGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#10253d" />
            <stop offset="40%" stopColor="#1467ac" />
            <stop offset="100%" stopColor="#3aa9ff" />
          </linearGradient>

          <linearGradient id="chipGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#182331" />
            <stop offset="50%" stopColor="#07111d" />
            <stop offset="100%" stopColor="#0e2941" />
          </linearGradient>

          <filter id="circuitGlow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* chip */}
        <g transform="translate(540 28) rotate(10)">
          <rect
            x="125"
            y="40"
            width="330"
            height="170"
            rx="7"
            fill="url(#chipGradient)"
            stroke="rgba(79,157,227,.35)"
          />

          <rect
            x="156"
            y="67"
            width="270"
            height="115"
            rx="4"
            fill="#08111c"
            stroke="rgba(65,157,234,.18)"
          />

          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <React.Fragment key={i}>
              <rect
                x={164 + i * 34}
                y="22"
                width="16"
                height="30"
                rx="2"
                fill="#1c496c"
              />
              <rect
                x={164 + i * 34}
                y="198"
                width="16"
                height="30"
                rx="2"
                fill="#184d75"
              />
            </React.Fragment>
          ))}
        </g>

        {/* traces */}
        <g
          fill="none"
          stroke="url(#traceGradient)"
          strokeWidth="2.2"
          opacity="0.8"
        >
          <path d="M550 235 H470 L415 290 H315" />
          <path d="M580 250 H500 L445 310 H335" />
          <path d="M610 266 H530 L475 332 H365" />
          <path d="M640 282 H565 L505 350 H402" />
          <path d="M685 290 V355 H750 V415" />
          <path d="M720 280 V337 H790 V400" />
          <path d="M755 270 V320 H830 V380" />
          <path d="M790 255 V302 H865 V360" />
        </g>

        <g fill="#31a5ff" filter="url(#circuitGlow)">
          <circle cx="315" cy="290" r="4" />
          <circle cx="335" cy="310" r="4" />
          <circle cx="365" cy="332" r="4" />
          <circle cx="402" cy="350" r="4" />

          <circle cx="750" cy="415" r="4" />
          <circle cx="790" cy="400" r="4" />
          <circle cx="830" cy="380" r="4" />
          <circle cx="865" cy="360" r="4" />
        </g>

        {/* digital particles */}
        <g fill="#148ee3" opacity="0.55">
          {Array.from({ length: 18 }).map((_, i) => (
            <circle
              key={i}
              cx={430 + (i % 6) * 45}
              cy={120 + Math.floor(i / 6) * 45}
              r="2.4"
            />
          ))}
        </g>
      </svg>

      <div className="growthX__container">
        {/* HEADER */}
        <div className="growthX__header">
          <div className="growthX__intro">
            <div className="growthX__label">
              <span />
              PROFESSIONAL GROWTH
            </div>

            <h2 className="growthX__title">
              Learn. Build. Grow. <strong>Lead.</strong>
            </h2>

            <p>
              Engineering is a continuous journey of learning, applying
              knowledge, and solving increasingly complex challenges. At
              Digicomm Semiconductor, we empower engineers to strengthen their
              technical expertise through hands-on projects, cross-functional
              collaboration, mentorship, and exposure to evolving technologies.
            </p>

            <p>
              We believe that every project is an opportunity to build deeper
              knowledge, stronger capabilities, and greater confidence.
            </p>
          </div>

          <div className="growthX__statement">
            <span>ENGINEER</span>
            <span>TODAY</span>
            <span>A BRIGHTER</span>
            <span>TOMORROW</span>

            <i />
          </div>
        </div>

        {/* JOURNEY */}
        <div className="growthX__journey">
          {/* glowing wave */}

          <svg
            className="growthX__path"
            viewBox="0 0 1500 280"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="growthLine" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#0877d8" />
                <stop offset="60%" stopColor="#34a8ff" />
                <stop offset="78%" stopColor="#e5eef7" />
                <stop offset="100%" stopColor="#e4ad26" />
              </linearGradient>

              <filter id="lineGlow">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path
              d="
                M10 230
                C130 160 220 160 320 175
                C430 195 495 125 610 145
                C720 170 770 105 885 115
                C1005 125 1060 35 1190 62
                C1290 85 1340 58 1455 25
              "
              fill="none"
              stroke="url(#growthLine)"
              strokeWidth="3"
              filter="url(#lineGlow)"
            />

            <path
              d="M1425 10 L1462 25 L1435 55"
              fill="none"
              stroke="#e4ad26"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* CARDS */}
          <div className="growthX__steps">
            {growthSteps.map((item, index) => {
              const Icon = item.icon;

              return (
                <article
                  className={`growthX__step growthX__step--${index + 1}`}
                  key={item.title}
                >
                  {/* ICON STATION */}

                  <div className="growthX__station">
                    <div className="growthX__iconBox">
                      <Icon size={38} strokeWidth={1.6} />
                    </div>

                    <div className="growthX__platform growthX__platform--top" />

                    <div className="growthX__platform growthX__platform--bottom" />

                    <div className="growthX__dot" />
                  </div>

                  {/* CARD */}

                  <div className="growthX__card">
                    <div className="growthX__number">{item.number}</div>

                    <h3>{item.title}</h3>

                    <span className="growthX__underline" />

                    <p>{item.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowthSection;